import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Plus } from "lucide-react";
import { cn } from "../../../lib/utils";
import AccountHolderIcon from "./AccountHolderIcon";
import { CreditAccountProduct } from "../../../api/credit_accounts";
import { FullStakeholderData } from "../../stakeholder/logic/stakeholderSlice";
import { EntityType, RELATED_AS_LABELS } from "../../../api/stakeholder_relationships";
import { useAppSelector } from "../../../app/hooks";
import { getAccountProductInfoByAccountNumberMap } from "../logic/slice";
import { getCaseAccounts } from "../../accounts/logic/slice";

export const normalizeName = (name: string): string =>
  name.replace(/\s+/g, "").toLowerCase();

export interface StakeholderOption extends FullStakeholderData {
  isNew: boolean;
}

interface StakeholderOptionButtonProps {
  stakeholderOption: StakeholderOption;
  onSelect: () => void
}

const StakeholderOptionButton: React.FC<StakeholderOptionButtonProps> = ({
  stakeholderOption,
  onSelect
}) => {
  const creditAccountByNumber = useAppSelector(getAccountProductInfoByAccountNumberMap);
  const accounts = useAppSelector(getCaseAccounts);
  const accountByNumber = useMemo(() => {
    const map: Record<string, { ext_account_number?: string }> = {
      ...creditAccountByNumber,
    };
    for (const account of accounts) {
      map[account.account_number] = {
        ext_account_number: account.ext_account_number,
      };
    }
    return map;
  }, [creditAccountByNumber, accounts]);

  const filteredRelationships = stakeholderOption.relationships.filter(
    (relationship) =>
      relationship.entity_type === EntityType.LOAN_ACCOUNT ||
      relationship.entity_type === EntityType.ACCOUNT,
  );

  return (
    <button
      type="button"
      className="flex flex-col gap-2 rounded-[6px] px-2 py-[6px] text-left hover:bg-[#F5F5F5] transition-colors self-stretch"
      onMouseDown={(e) => {
        e.preventDefault();
        onSelect();
      }}
    >
      <div className="flex items-center justify-between self-stretch">
        <span className="text-[14px] leading-[20px] font-normal text-[#0A0A0A] truncate">
          {stakeholderOption.stakeholder.name}
        </span>
        <span className="text-[12px] leading-[16px] font-normal text-[#737373] font-sans">
          {stakeholderOption.enriched_stakeholder?.customer_details?.cif_number?.trim() || "--"}
        </span>
      </div>

      {stakeholderOption.relationships.length > 0 && (
        <div className="flex flex-col gap-[2px] self-stretch min-w-0">
          {filteredRelationships.map((rel, idx) => {
            const accountLabel =
              accountByNumber[rel.entity_id]?.ext_account_number || rel.entity_id || "--";
            const text = `${accountLabel} • ${RELATED_AS_LABELS[rel.related_as]}`;
            return (
              <span
                key={idx}
                title={text}
                className="text-[12px] leading-[16px] font-normal text-[#737373] truncate block"
              >
                {text}
              </span>
            );
          })}
        </div>
      )}
    </button>
  )
}

interface AccountNameSearchInputProps {
  accountName: string;
  selectedStakeholderId?: string;
  options: StakeholderOption[];
  onSelectStakeholder: (stakeholderId: string) => void;
  onAddStakeholder: (name: string) => void;
  product?: string;
  disabled?: boolean;
  error?: string;
  hideLabel?: boolean;
  required?: boolean;
  placeholder?: string;
  roleLabel?: string;
  onBlur?: () => void;
}

const AccountNameSearchInput: React.FC<AccountNameSearchInputProps> = ({
  accountName,
  selectedStakeholderId,
  options,
  onSelectStakeholder,
  onAddStakeholder,
  product,
  disabled,
  error,
  hideLabel,
  required,
  placeholder = "Search or add new",
  roleLabel = "Primary Borrower",
  onBlur,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null);

  const inputContainerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const selectedStakeholder = selectedStakeholderId ? options.find((option) => option.stakeholder.stakeholder_id === selectedStakeholderId): undefined;

  const [query, setQuery] = useState(accountName);

  useEffect(() => {
    if(selectedStakeholder)
      setQuery(selectedStakeholder.stakeholder.name);
  }, [selectedStakeholder]);

  const updateMenuPosition = () => {
    const anchor = inputContainerRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();

    const estimatedMenuHeight = 280;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const shouldFlipToTop = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow;

    const baseStyle: React.CSSProperties = {
      position: "fixed",
      left: rect.left,
      width: Math.max(rect.width, 393),
      zIndex: 99999,
    };

    if (shouldFlipToTop) {
      baseStyle.top = rect.top - 4;
      baseStyle.transform = "translateY(-100%)";
    } else {
      baseStyle.top = rect.bottom + 4;
    }

    setMenuStyle(baseStyle);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        inputContainerRef.current &&
        !inputContainerRef.current.contains(target) &&
        popoverRef.current &&
        !popoverRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      updateMenuPosition();
      window.addEventListener("scroll", updateMenuPosition, true);
      window.addEventListener("resize", updateMenuPosition);
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      window.removeEventListener("scroll", updateMenuPosition, true);
      window.removeEventListener("resize", updateMenuPosition);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  

  const filteredOptions = useMemo(() => {
    const normalizedQuery = normalizeName(query);
    if (!normalizedQuery) return [];
    return options.filter((opt) => normalizeName(opt.stakeholder.name).includes(normalizedQuery));
  }, [options, query]);

  const handleSelectExisting = (opt: StakeholderOption) => {
    setQuery(opt.stakeholder.name);
    onSelectStakeholder(opt.stakeholder.stakeholder_id);
    setIsOpen(false);
    onBlur?.();
  };

  const handleAddNew = (name: string) => {
    const trimmed = name.trim();
    setQuery(trimmed);
    onAddStakeholder(trimmed);
    setIsOpen(false);
    onBlur?.();
  };

  const shouldShowPopover = isOpen && !disabled && query.trim().length > 0;

  return (
    <div className="flex flex-col gap-1 min-w-0" ref={inputContainerRef}>
      <label className={cn("text-xs font-medium text-gray-700", hideLabel && "sr-only")}>
        Account Name
        {required && <span className="text-red-500"> *</span>}
      </label>

      <div
        className={cn(
          "flex items-center border rounded-md bg-white h-9 px-3 gap-2",
          error ? "border-red-400" : "border-[#E5E5E5]",
          disabled && "bg-gray-100 cursor-not-allowed"
        )}
      >
        {product && (
          <span className="shrink-0 text-gray-500">
            <AccountHolderIcon product={product as CreditAccountProduct} />
          </span>
        )}
        <input
          type="text"
          value={query}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full text-sm outline-none bg-transparent placeholder:text-[#737373] disabled:cursor-not-allowed"
          onFocus={() => {
            updateMenuPosition();
            if (query.trim().length > 0) setIsOpen(true);
          }}
          onChange={(e) => {
            const val = e.target.value;
            setQuery(val);
            if (selectedStakeholderId && val !== selectedStakeholder?.stakeholder.name) {
              onSelectStakeholder("");
            }
            updateMenuPosition();
            setIsOpen(val.trim().length > 0);
          }}
          onBlur={onBlur}
        />
      </div>

      {selectedStakeholder?.isNew && (
        <span className="text-[12px] leading-[16px] font-normal text-[#737373]">
          This entity will be created as a new {roleLabel.toLowerCase()} in this case.
        </span>
      )}

      {error && <span className="text-xs text-red-500">{error}</span>}

      {shouldShowPopover &&
        menuStyle &&
        ReactDOM.createPortal(
          <div
            ref={popoverRef}
            style={{
              ...menuStyle,
              boxShadow:
                "0 4px 6px -1px rgba(0, 0, 0, 0.10), 0 2px 4px -2px rgba(0, 0, 0, 0.10)",
            }}
            className="flex flex-col gap-2 rounded-[8px] border border-[#E5E5E5] bg-[#FFFFFF] p-1 max-h-[280px] overflow-y-auto"
          >
            {filteredOptions.length > 0 && (
              <div className="flex flex-col gap-2">
                {filteredOptions.map((opt) => (
                  <StakeholderOptionButton
                    key={opt.stakeholder.stakeholder_id}
                    stakeholderOption={opt}
                    onSelect={() => {
                      handleSelectExisting(opt);
                    }}
                  />
                ))}
              </div>
            )}

            <button
              type="button"
              className="flex items-center gap-2 self-stretch rounded-[8px] px-2 py-[6px] text-[12px] leading-[16px] font-medium text-[#3551F3]"
              onMouseDown={(e) => {
                e.preventDefault();
                handleAddNew(query);
              }}
            >
              <Plus width={14} height={14} className="shrink-0" />
              <span>Add “{query.trim()}” as a new {roleLabel}</span>
            </button>
          </div>,
          document.body
        )}
    </div>
  );
};

export default AccountNameSearchInput;